const API_URL = import.meta.env.VITE_API_URL || 'https://api.dilzee.com';

// ─── Session ID ────────────────────────────────────────────────────────────
export function getSessionId(): string {
  let id = sessionStorage.getItem('dilzee_session_id');
  if (!id) {
    id = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('dilzee_session_id', id);
  }
  return id;
}

// ─── Event queue ───────────────────────────────────────────────────────────
interface QueuedEvent {
  event: string;
  sessionId: string;
  productId?: string;
  path?: string;
  referrer?: string;
  timestamp: number;
  amount?: number;
}

let eventQueue: QueuedEvent[] = [];
let currentSubdomain = '';
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Flush the queue by sending all accumulated events in one request
export async function flushQueue() {
  if (eventQueue.length === 0 || !currentSubdomain) return;

  const toSend = [...eventQueue];
  eventQueue = [];

  try {
    await fetch(`${API_URL}/public/${currentSubdomain}/track-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        events: toSend.map(evt => ({
          event: evt.event,
          sessionId: evt.sessionId,
          productId: evt.productId || '',
          path: evt.path || '/',
          referrer: evt.referrer || 'direct',
          amount: evt.amount || 0,
        }))
      }),
      keepalive: true, // Ensures request completes even if page unloads
    });
  } catch (e) {
    // Analytics errors must never affect the user experience
  }
}

// Schedule a deferred flush after a short delay (debounced)
function scheduleFlushed(delayMs = 3000) {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, delayMs);
}

// Flush immediately on page unload or visibility change so we don't lose events
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Page is being hidden/closed — flush immediately
      flushQueue();
    }
  });

  window.addEventListener('beforeunload', () => {
    flushQueue();
  });

  // Proactive periodic flush every 30 seconds so events reach the server
  // reliably even if the user stays on the page for a long time or
  // closes the tab without triggering beforeunload (common on mobile)
  setInterval(() => {
    flushQueue();
  }, 30_000);
}

// ─── Public API ────────────────────────────────────────────────────────────
export async function track(
  subdomain: string,
  event: 'page_view' | 'product_view' | 'add_to_cart' | 'checkout_start' | 'order_complete',
  data: { productId?: string; path?: string; amount?: number } = {}
) {
  try {
    currentSubdomain = subdomain;

    // Buffer ALL events into the queue to keep invocations as low as possible.
    eventQueue.push({
      event,
      sessionId: getSessionId(),
      productId: data.productId,
      path: data.path || window.location.pathname,
      referrer: document.referrer || 'direct',
      timestamp: Date.now(),
      amount: data.amount,
    });

    // For high-value events (page_view, order_complete), flush soon after
    // queuing so we don't rely solely on tab-close to deliver them.
    if (event === 'page_view' || event === 'order_complete') {
      scheduleFlushed(3000);
    }
  } catch {
    // Analytics errors must never affect the user experience
  }
}

